import React, { useEffect, useState } from 'react';
import '../../styles/onboardingFlow/onboarding/onboardingBusinessType.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/store';
import { useTranslation } from 'react-i18next';
import BusinessTypeSelector from '../onboardingComponents/BusinessTypeSelector';
import AIPhoneInput from '../../ui/inputs/AIPhoneInput';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import HandWavingIcon from '../../assets/onboarding/common/HandWaving.svg';
import LightBulbIcon from '../../assets/onboarding/common/light-bulb.svg';
import OnboardingLogoPanel from '../onboardingComponents/OnboardingLogoPanel';
import OnboardingFooter from '../onboardingComponents/OnboardingFooter';
import {
  setAiOnboardingInprogress,
  setAiOnboardingStepCompleted,
  setCompanyStepDraft,
  setIsAiOnboardingCompleted,
  updateProfile,
} from '../../store/reducers/userSlice';
import { asyncHandler } from '../../lib/api/async-handler';
import { RequestInfo } from '../../interfaces/RequestInfo';
import { REGISTRATION_API } from '../../lib/constants/central-api';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { submitLogToBugsnag } from '../../lib/api/log';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import getCountryByDialCode from '../../lib/helper-functions/getCountryByDialCode';
import update_all_shop_stats_api_call from '../../lib/api/dashboard/update_all_shop_stats_api_call';
import { markOnboardingComplete } from '../../lib/helper-functions/onboardingCompletion';
import { HOME_ROUTE } from '../../lib/constants/route';
import Popup from '../../ui/popup/Popup';

const OnboardingBusinessType: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const accessToken = useSelector(
    (state: RootState) => state.auth.authorization.accessToken
  );
  const seller_id = useSelector(
    (state: RootState) => state.user.profile.seller_id
  );
  const autoSelectAgencyRole = useSelector(
    (state: RootState) => state.UI.autoSelectAgencyRole
  );
  const preUserInvitation = useSelector(
    (state: RootState) => state.user.preUserInvitation
  );
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const IPCountry = useSelector(
    (state: RootState) => state.app.countries.countryOfIP
  );
  const countries = useSelector(
    (state: RootState) => state.app.countries.available
  );
  const appLanguage = useSelector((state: RootState) => state.app.language);
  const merchantSlug = useSelector(
    (state: RootState) => state.user.merchant.merchant_slug
  );
  const companyStepDraft = useSelector((state: RootState) => {
    const aiOnboarding = state.user.ai_onboarding as {
      companyStepDraft?: {
        userRole: 'seller' | 'agency';
      } | null;
    };
    return aiOnboarding.companyStepDraft ?? null;
  });
  const allShopsFromProfile = useSelector(
    (state: RootState) => state.user.profile.all_shops
  );

  const getInitialRole = (): 'seller' | 'agency' => {
    const profileRole = userProfile?.role;
    if (profileRole === 'agency' || profileRole === 'seller')
      return profileRole;
    if (companyStepDraft?.userRole) return companyStepDraft.userRole;
    return autoSelectAgencyRole ? 'agency' : 'seller';
  };

  const [loading, setLoading] = useState<'pending' | 'initial'>('initial');
  const [userRole, setUserRole] = useState<'seller' | 'agency'>(
    getInitialRole()
  );
  const [phoneNumber, setPhoneNumber] = useState({
    number: userProfile.phone,
    country: userProfile.country_code
      ? getCountryByDialCode(userProfile.country_code)
      : countries[IPCountry],
  });
  const [showCannotSwitchPopup, setShowCannotSwitchPopup] = useState(false);

  const hasShopsInProfile =
    !!allShopsFromProfile &&
    Array.isArray(allShopsFromProfile) &&
    allShopsFromProfile.length > 0;
  const disabledRoleTypes: ('business' | 'agency')[] = hasShopsInProfile
    ? userRole === 'seller'
      ? ['agency']
      : ['business']
    : [];

  useEffect(() => {
    const profileRole = userProfile?.role;
    if (profileRole === 'agency' || profileRole === 'seller') {
      setUserRole(profileRole);
    }
  }, [userProfile?.role]);

  useEffect(() => {
    if (preUserInvitation.id && preUserInvitation.data) {
      if (preUserInvitation.data?.seller_type) {
        const role =
          preUserInvitation.data?.seller_type === 'agency'
            ? 'agency'
            : 'seller';
        setUserRole(role);
      }
    }
  }, [preUserInvitation]);

  useEffect(() => {
    dispatch(
      setCompanyStepDraft({
        userRole,
      })
    );
  }, [userRole, dispatch]);

  async function submitProfile() {
    const hasPhone = !!phoneNumber.number && isNotEmptyString(phoneNumber.number);
    const sendingData: Record<string, any> = {
      language: isNotEmptyString(userProfile?.language)
        ? userProfile?.language
        : isNotEmptyString(appLanguage)
        ? appLanguage
        : 'en',
    };
    // Phone is now optional in the streamlined first-run; only persist it when
    // the user actually filled it in.
    if (hasPhone) {
      sendingData.contact = phoneNumber.number;
      sendingData.country_code = phoneNumber.country?.dialCode?.substring(1);
    }
    try {
      const requestInfo: RequestInfo = {
        url: `${REGISTRATION_API(merchantSlug)}/${seller_id}`,
        method: 'PUT',
        body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
          Authorization: `${accessToken.type} ${accessToken.sellerToken}`,
        },
      };

      const actionType = 'ONBOARDING_PROFILE_UPDATE';
      const customMsg = 'Profile Update Successful.';

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false,
        false,
        true
      );

      const response = res.data;
      if (res.actionType === actionType && response.success === true) {
        const { user } = response?.data;
        if (user?.seller_id) {
          dispatch(
            updateProfile({
              role: userRole,
              ...(hasPhone
                ? {
                    country_code:
                      phoneNumber.country?.dialCode?.substring(1),
                    phone: phoneNumber.number,
                  }
                : {}),
              language: isNotEmptyString(userProfile?.language)
                ? userProfile?.language
                : isNotEmptyString(appLanguage)
                ? appLanguage
                : 'en',
            })
          );
          return true;
        }
      } else {
        setLoading('initial');
        return false;
      }
    } catch (exception) {
      setLoading('initial');
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }

  // Account type is the only onboarding step. On submit we persist the
  // profile/role, then COMPLETE onboarding and drop the user into the app.
  //
  // Completion is written in two places so a reload can't re-trap the user:
  //  1. Redux flags (persisted via redux-persist whitelist ['user']) — this is
  //     the flag App.tsx gates on. Set is_ai_onboarding_inprogress=false.
  //  2. Backend shop stats (is_dashboard_onboarding_complete=true) — this is
  //     what `onboarding-state-updates.ts` re-derives from on app load. It is
  //     shop-scoped, so it only persists for users that already have a shop;
  //     brand-new no-shop users have no shop record for state-updates to read,
  //     so the redux flag alone keeps them out of onboarding. Mirrors the old
  //     AskTara completion (`update_all_shop_stats_api_call` + redux flag).
  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading('pending');

    try {
      const profileUpdated = await submitProfile();
      if (!profileUpdated) throw new Error('Profile update failed');

      // Best-effort backend completion write. Returns false (no-op) when the
      // user has no shop yet — that's fine, the redux flag below is the gate.
      try {
        await update_all_shop_stats_api_call(1, true);
      } catch (statsError) {
        submitLogToBugsnag('warning', `Onboarding complete stats write: ${statsError}`);
      }

      dispatch(
        setAiOnboardingStepCompleted({ stepNumber: 1, isCompleted: true })
      );
      dispatch(setIsAiOnboardingCompleted(true));
      dispatch(setAiOnboardingInprogress(false));

      // Durable, seller-scoped completion marker that survives logout (the
      // streamlined flow creates no shop, so there's no backend completion
      // record yet). Prevents the next login from re-trapping into onboarding.
      markOnboardingComplete(seller_id);

      setLoading('initial');
      navigate(HOME_ROUTE, { replace: true });
    } catch (error) {
      console.error('Error during submission:', error);
      setLoading('initial');
    }
  };

  // Phone is optional now — never let a missing phone block continuing.
  const isDisabled = false;

  return (
    <form onSubmit={submitForm} className='onboardingBusinessType'>
      <OnboardingLogoPanel />
      <div className='onboardingBusinessType__header'>
        <div className='onboardingBusinessType__header-row'>
          <div className='onboardingBusinessType__header-icon'>
            <img src={HandWavingIcon} alt='Welcome' />
          </div>
          <h1 className='onboardingBusinessType__title'>
            Welcome to ADUP — let's tailor it to you
          </h1>
        </div>
        <p className='onboardingBusinessType__subtitle'>
          Tell us how you'll use ADUP and we'll set up the right experience for
          you.
        </p>
      </div>

      <BusinessTypeSelector
        selectedType={userRole === 'seller' ? 'business' : 'agency'}
        onSelect={(type) =>
          setUserRole(type === 'business' ? 'seller' : 'agency')
        }
        disabledTypes={disabledRoleTypes}
        onDisabledTypeClick={() => setShowCannotSwitchPopup(true)}
      />

      <div className='onboardingBusinessType__section'>
        <h2 className='onboardingBusinessType__section-title'>
          Contact number (optional)
        </h2>
        <div className='onboardingBusinessType__input'>
          <AIPhoneInput
            id='onboardingBusinessType__user-phone-input'
            setPhoneNumber={setPhoneNumber}
            phoneNumber={phoneNumber}
            label=''
            placeholder='Enter your phone number'
            title={t('Phone-validation')}
            required={false}
            pattern='^[0-9]{4,}$'
          />
        </div>
      </div>

      <div className='onboardingBusinessType__footer'>
        <MainSquareButton
          type='submit'
          value='Continue to ADUP'
          normal={true}
          loading={loading === 'pending'}
          disabled={isDisabled}
          style={{
            width: '100%',
            paddingTop: '1.2rem',
            paddingBottom: '1.2rem',
          }}
        />
      </div>

      <OnboardingFooter />

      <Popup
        trigger={showCannotSwitchPopup}
        setTrigger={() => setShowCannotSwitchPopup(false)}
      >
        <div className='onboardingBusinessType__cannot-switch-popup'>
          <div className='onboardingBusinessType__cannot-switch-popup-header'>
            <img
              src={LightBulbIcon}
              alt=''
              className='onboardingBusinessType__cannot-switch-popup-icon'
            />
            <h3 className='onboardingBusinessType__cannot-switch-popup-title'>
              Can't switch
            </h3>
          </div>
          <div className='onboardingBusinessType__cannot-switch-popup-divider' />
          <div className='onboardingBusinessType__cannot-switch-popup-body'>
            <p className='onboardingBusinessType__cannot-switch-popup-text'>
              After creating a shop, you cannot switch between Business and
              Agency.
            </p>
            <button
              type='button'
              className='onboardingBusinessType__cannot-switch-popup-button'
              onClick={() => setShowCannotSwitchPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      </Popup>
    </form>
  );
};

export default OnboardingBusinessType;
